import React, { useState, useEffect } from 'react';
import { BringzzIcon, BringzzText } from "@bringzz/components";
import { useNavigation } from "context/NavigationContext";
import useRequest from 'hooks/useRequest';

const BringzzShopperCardSmall = ({ shopper }) => {
    const { navigate } = useNavigation();
    const { sendRequest, data, error, loading } = useRequest();
    const [showReviewModal, setShowReviewModal] = useState(false);

    const [rating, setRating] = useState(0);
    const [reviewMessage, setReviewMessage] = useState('');
    const { sendRequest: fetchExistingReview, data: existingReview, loading: loadingExisting, error: errorExisting } = useRequest();
    const { sendRequest: sendReview, data: reviewResponse, loading: sendingReview, error: errorSending } = useRequest();

    useEffect(() => {
        const fetchShopperReview = async () => {
            if (shopper && shopper.integrationId) {
                await sendRequest({
                    method: 'GET',
                    url: `/module/shopperreview/getShopperRatingSummary?shopperUuid=${shopper.integrationId}`,
                });
            }
        };
        fetchShopperReview();
    }, [shopper, sendRequest]);

    useEffect(() => {
        const fetchUserReview = async () => {
            if (shopper && shopper.integrationId && showReviewModal) {
                setRating(0);
                setReviewMessage('');

                await fetchExistingReview({
                    method: 'GET',
                    url: `/module/shopperreview/getUserReviewStatus?shopperUuid=${shopper.integrationId}`,
                });
            }
        };
        fetchUserReview();
    }, [shopper, showReviewModal, fetchExistingReview]);

    useEffect(() => {
        if (existingReview && existingReview.data) {
            setRating(existingReview.data.rating || 0);
            setReviewMessage(existingReview.data.review || '');
        } else if (existingReview && existingReview.status === "error") {
            setRating(0);
            setReviewMessage('');
        }
    }, [existingReview]);

    const handleStarClick = (starIndex) => {
        setRating(starIndex);
    };

    const handleSubmitReview = async () => {
        if (!shopper || !shopper.integrationId || rating === 0) {
            alert('Please provide a rating and ensure shopper information is available.');
            return;
        }

        await sendReview({
            method: 'POST',
            url: '/module/shopperreview/createOrUpdateReview',
            headers: {
                'Content-Type': 'application/json',
            },
            data: {
                shopperUuid: shopper.integrationId,
                rating: rating,
                review: reviewMessage,
            },
        });

    };

    if (!shopper) {
        return (
            <div className='flex space-x-4 border-b py-1 text-black/20'>
                <div>
                    <BringzzIcon
                        folder='HealthIcons'
                        icon='IconPharmacy'
                        size='22'
                        className='p-3 rounded-full' />
                </div>
                <div className='flex w-full items-center'>
                    <BringzzText tag='h3' className='text-black/20'>Shopper</BringzzText>
                </div>
            </div>
        );
    }

    return (
        <div className='flex space-x-4 border-b py-2'>
            <div>
                <BringzzIcon
                    folder='HealthIcons'
                    icon='IconPharmacy'
                    size='22'
                    className='p-3 rounded-full' />
            </div>
            <div className='flex w-full justify-between'>
                <div>
                    <BringzzText tag='h3'>Shopper</BringzzText>
                    <div className='flex items-center space-x-2'>
                        <BringzzText tag='h4' className={"font-light"}>{shopper.firstName || "shopper_name"}</BringzzText>
                        {data && data.data && data.data.formatted && (
                            <div className='flex items-center space-x-1'>
                                <BringzzIcon folder='LineIcons' icon='IconStarFill' size='16' />
                                <BringzzText tag='h4' className={"text-sm text-gray-500"}>{data.data.formatted}</BringzzText>
                            </div>
                        )}
                    </div>
                </div>
                <div className='flex items-center space-x-2'>
                    <BringzzIcon
                        folder='LineIcons'
                        icon='IconInfoCircle'
                        size='22'
                        className='cursor-pointer'
                        onClick={() => setShowReviewModal(true)}
                    />
                    <BringzzIcon
                        folder='LineIcons'
                        icon='IconPhone'
                        size='16'
                        className='cursor-pointer'
                        onClick={() => {
                            window.open(`tel:${shopper.phoneNumber}`, '_blank');
                        }} />
                    <BringzzIcon
                        folder='LineIcons'
                        icon='IconSupport'
                        size='21'
                        className='cursor-pointer'
                        onClick={() => {
                            navigate(`/settings/chats/${shopper.id}`);
                        }} />
                </div>
            </div>

            {showReviewModal && (
                <div className="fixed inset-0 bg-white bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-sm mx-4 shadow-lg">
                        <div className="flex justify-between items-center mb-4">
                            <BringzzText tag="h2" className="text-xl font-semibold">Rate {shopper.firstName || 'Shopper'}</BringzzText>
                            <BringzzIcon
                                folder='LineIcons'
                                icon='IconClose'
                                size='24'
                                className='cursor-pointer'
                                onClick={() => setShowReviewModal(false)}
                            />
                        </div>

                        <div className="flex justify-center mb-4">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <BringzzIcon
                                    key={star}
                                    folder='LineIcons'
                                    icon={star <= rating ? 'IconStarFill' : 'IconStar'}
                                    size='32'
                                    className='cursor-pointer text-yellow-500'
                                    onClick={() => handleStarClick(star)}
                                />
                            ))}
                        </div>

                        <textarea
                            className="w-full p-2 border rounded-md mb-4 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows="4"
                            placeholder="Write your review here..."
                            value={reviewMessage}
                            onChange={(e) => setReviewMessage(e.target.value)}
                        ></textarea>

                        <button
                            className="w-full bg-blue-500 text-white py-2 rounded-md flex items-center justify-center space-x-2 hover:bg-blue-600 transition duration-200"
                            onClick={handleSubmitReview}
                            disabled={sendingReview}
                        >
                            {sendingReview ? 'Sending...' : (
                                <>
                                    <BringzzIcon folder='LineIcons' icon='IconSend' size='20' />
                                    <BringzzText tag='span'>Send Review</BringzzText>
                                </>
                            )}
                        </button>

                        {(loadingExisting || errorExisting || errorSending) && (
                            <div className="mt-4 text-center text-red-500">
                                {loadingExisting && "Loading existing review..."}
                                {errorExisting && `Error loading review: ${errorExisting.message}`}
                                {errorSending && `Error sending review: ${errorSending.message}`}
                            </div>
                        )}
                        {reviewResponse && reviewResponse.message && (
                            <div className="mt-4 text-center text-green-500">
                                {reviewResponse.message}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default BringzzShopperCardSmall;